import React, { useCallback, useId, useRef, useState } from 'react';
import './FileDropZone.css';

export interface FileDropZoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  capture?: 'environment' | 'user';
  /** Stable id for labels and programmatic reset (e.g. clear form). */
  id?: string;
  className?: string;
  /** Primary line; default invites drag + browse. */
  prompt?: React.ReactNode;
  hint?: string;
  maxFileSizeBytes?: number;
  onReject?: (message: string) => void;
  variant?: 'default' | 'compact';
}

/**
 * Drop files onto the zone or click / keyboard-activate to open the file picker.
 */
const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFiles,
  accept,
  multiple = false,
  disabled,
  capture,
  id: idProp,
  className = '',
  prompt,
  hint,
  maxFileSizeBytes,
  onReject,
  variant = 'default',
}) => {
  const reactId = useId().replace(/:/g, '');
  const inputId = idProp ?? `file-drop-${reactId}`;
  const [dragActive, setDragActive] = useState(false);
  const dragDepth = useRef(0);

  const filterBySize = useCallback(
    (files: File[]): File[] => {
      if (!maxFileSizeBytes) return files;
      const ok: File[] = [];
      for (const f of files) {
        if (f.size > maxFileSizeBytes) {
          onReject?.(`“${f.name}” is too large for this upload.`);
        } else {
          ok.push(f);
        }
      }
      return ok;
    },
    [maxFileSizeBytes, onReject]
  );

  const emitFiles = useCallback(
    (list: FileList | File[] | null) => {
      if (!list || list.length === 0) return;
      const files = filterBySize(Array.from(list));
      if (files.length > 0) onFiles(files);
    },
    [filterBySize, onFiles]
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emitFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setDragActive(false);
    if (disabled) return;
    emitFiles(e.dataTransfer.files);
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    dragDepth.current += 1;
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragActive(false);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) e.dataTransfer.dropEffect = 'copy';
  };

  const rootClass = [
    'file-drop-zone',
    `file-drop-zone--${variant}`,
    dragActive ? 'file-drop-zone--drag-active' : '',
    disabled ? 'file-drop-zone--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const defaultPrompt = (
    <>
      Drag files here or <span className="file-drop-zone__browse-word">browse</span>
    </>
  );

  return (
    <div
      className={rootClass}
      onDrop={onDrop}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
    >
      <input
        id={inputId}
        type="file"
        className="file-drop-zone__input"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        capture={capture}
        onChange={onInputChange}
        aria-describedby={hint ? `${inputId}-hint` : undefined}
      />
      <label htmlFor={inputId} className="file-drop-zone__label">
        <span className="file-drop-zone__prompt">{prompt ?? defaultPrompt}</span>
        {hint ? (
          <span id={`${inputId}-hint`} className="file-drop-zone__hint">
            {hint}
          </span>
        ) : null}
      </label>
    </div>
  );
};

export default FileDropZone;
